import React, { useState, useEffect } from 'react'
import { Zap, Plus, Trash2, Edit, ToggleLeft, ToggleRight } from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'

interface AutomationRule {
  id: string
  name: string
  enabled: boolean
  trigger: string
  conditions: any[]
  action: any
}

const AutomationRulesPage = () => {
  const { currentTenant } = useTenant()
  const [rules, setRules] = useState<AutomationRule[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (currentTenant) {
      fetchRules()
    }
  }, [currentTenant])

  const fetchRules = async () => {
    try {
      const response = await fetch(`/api/automation-rules?tenantId=${currentTenant!.id}`)
      if (response.ok) {
        const data = await response.json()
        setRules(data.rules)
      }
    } catch (error) {
      console.error('Error fetching rules:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleRule = async (ruleId: string, enabled: boolean) => {
    if (!currentTenant) return

    const rule = rules.find(r => r.id === ruleId)
    if (!rule) return

    try {
      const response = await fetch(`/api/automation-rules?tenantId=${currentTenant.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...rule, enabled: !enabled })
      })

      if (!response.ok) {
        const errorBody = await response.text()
        throw new Error(`Failed to toggle rule: ${response.status} - ${errorBody}`)
      }

      fetchRules()
    } catch (error) {
      console.error('Error toggling rule:', error)
      alert(`Failed to toggle rule: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const deleteRule = async (ruleId: string) => {
    if (!currentTenant) return
    if (!confirm('Are you sure you want to delete this rule?')) return

    try {
      const response = await fetch(`/api/automation-rules?tenantId=${currentTenant.id}&ruleId=${ruleId}`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        const errorBody = await response.text()
        throw new Error(`Failed to delete rule: ${response.status} - ${errorBody}`)
      }

      fetchRules()
    } catch (error) {
      console.error('Error deleting rule:', error)
      alert(`Failed to delete rule: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Zap className="h-8 w-8 text-primary-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">Automation Rules</h1>
            </div>
            <a href="/admin" className="text-primary-600 hover:text-primary-700">← Back</a>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Example Rules</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>• Auto-approve access requests from @google.com email addresses</li>
            <li>• Notify sales team when users from Fortune 500 companies visit</li>
            <li>• Auto-approve users who download 3+ compliance documents</li>
            <li>• Send alert when high-engagement prospects request access</li>
          </ul>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <button className="btn-primary inline-flex items-center">
              <Plus className="h-4 w-4 mr-2" />
              Create Rule
            </button>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          ) : rules.length === 0 ? (
            <div className="p-12 text-center">
              <Zap className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No automation rules configured yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {rules.map(rule => (
                <div key={rule.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">{rule.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Trigger: {rule.trigger} • Action: {rule.action.type}
                      </p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => toggleRule(rule.id, rule.enabled)}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        {rule.enabled ? (
                          <ToggleRight className="h-6 w-6 text-green-600" />
                        ) : (
                          <ToggleLeft className="h-6 w-6 text-gray-400" />
                        )}
                      </button>
                      <button className="text-gray-600 hover:text-gray-800">
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => deleteRule(rule.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AutomationRulesPage
